/**
 * Phase 8.12 — Screen 35: public 1-v-1 duels browser.
 *
 * Layout (per wireframe):
 *   1. "Your turn · play now" pinned group — the viewer's duels where
 *      the ball is in their court (`awaiting_opponent`, opponent_id=me),
 *      sorted by nearest deadline. Each row is a bright Play button.
 *   2. Filter row: topic pill scroll + stake pill row. Filters apply
 *      to the "Open to join" section only.
 *   3. "Open to join" list — open public duels not yet claimed, sorted
 *      by nearest publish-expiry. Row shows creator + rank + topic +
 *      entry + Join.
 *   4. Footer: "Unaccepted duels auto-expire after 24h — stakes refunded."
 *
 * The Home strip and this page reuse the SAME row rendering (see
 * `duelRowHtml`) so the visual language matches. The "already taken"
 * state is surfaced inline when a Join returns 409 — the row swaps in
 * place rather than pushing a toast, matching the wireframe intent.
 */

import { api, ApiError } from '../api.js';
import { escape } from '../components/form.js';
import { toast } from '../components/toast.js';
import { navigate } from '../router.js';
import { formatKobo } from '../wallet.js';

const STAKE_FILTERS = [
  { key: 'any', label: 'Any stake' },
  { key: '0', label: 'Free', kobo: [0, 0] },
  { key: '20000', label: '₦200', kobo: [20_000, 20_000] },
  { key: '50000', label: '₦500', kobo: [50_000, 50_000] },
];

export async function renderDuelsBrowse(root) {
  root.innerHTML = `
    <div class="appbar">
      <a href="/" data-link class="appbar-back" aria-label="Back">‹</a>
      <div class="appbar-title">Public duels</div>
      <div style="flex:1"></div>
      <a href="/duels/new" data-link class="tag violet" style="cursor:pointer">+ Create</a>
    </div>
    <div class="pad" id="browse-page">
      <div class="skel"></div>
      <div class="skel"></div>
      <div class="skel"></div>
    </div>
  `;

  const page = document.getElementById('browse-page');
  const state = {
    categoryId: null,
    categories: [],
    stakeFilter: 'any',
    open: [],
    yourTurn: [],
  };

  try {
    state.categories = await api('/game/categories');
  } catch {
    /* fall through — filters render without topic pills */
  }

  await refresh(page, state);
}

async function refresh(page, state) {
  const params = new URLSearchParams();
  params.set('limit', '20');
  if (state.categoryId) params.set('category_id', state.categoryId);
  const stake = STAKE_FILTERS.find((f) => f.key === state.stakeFilter);
  if (stake && stake.kobo) {
    params.set('stake_min', String(stake.kobo[0]));
    params.set('stake_max', String(stake.kobo[1]));
  }

  let openList, mineList;
  try {
    [openList, mineList] = await Promise.all([
      api(`/duels/public?${params.toString()}`),
      api('/duels/mine?limit=20'),
    ]);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      navigate('/login');
      return;
    }
    page.innerHTML = `<div class="card" style="color:var(--pink-t)">${escape(err.message)}</div>`;
    return;
  }

  state.open = openList.items || [];
  const mine = mineList.items || [];
  // Your turn: awaiting opponent AND the viewer is the opponent (the
  // creator has played, now it's my move).
  state.yourTurn = mine.filter(
    (d) => d.status === 'awaiting_opponent' && d.viewer_role === 'opponent',
  );
  // Waiting on someone else: I created it and either the opponent
  // hasn't accepted (open) or they've claimed but haven't played
  // (awaiting_opponent when I'm the creator, or `matched`).
  // Prevents the "I made a duel, played, then can't find it anywhere"
  // dead-end.
  state.waitingOnOthers = mine.filter((d) => {
    if (d.viewer_role !== 'creator') return false;
    return d.status === 'open' || d.status === 'awaiting_opponent' || d.status === 'matched';
  });

  page.innerHTML = `
    ${state.yourTurn.length ? `
      <h3 class="sec"><span class="dot" style="background:#F7C667"></span>Your turn · play now</h3>
      ${state.yourTurn.map((d) => duelRowHtml(d, 'your-turn')).join('')}
    ` : ''}

    ${state.waitingOnOthers.length ? `
      <h3 class="sec"><span class="dot" style="background:#7FB4FF"></span>Waiting on someone</h3>
      ${state.waitingOnOthers.map((d) => duelRowHtml(d, 'waiting')).join('')}
    ` : ''}

    ${renderFilterRow(state)}

    <h3 class="sec"><span class="dot" style="background:#5FE0BE"></span>Open to join</h3>
    ${state.open.length
      ? state.open.map((d) => duelRowHtml(d, 'open')).join('')
      : `<div class="card muted">No public duels right now.
           <a href="/duels/new" data-link>Publish one</a> and be first.</div>`}
    <div class="center" style="font-size:11px;color:var(--mute);font-weight:500;margin-top:8px">
      Unaccepted duels auto-expire after 24h — stakes refunded.
    </div>
  `;

  wireFilterHandlers(page, state);
  wireRowHandlers(page, state);
}

function renderFilterRow(state) {
  // Topic filter is a native <select> so every category is reachable
  // (the prior pill row was hard-capped at 6 and hid the rest behind
  // an overflow-scroll). Native picker also gets us the OS-provided
  // large-list UI on mobile for free.
  const topicOptions = [
    `<option value="">All topics</option>`,
    ...state.categories.map(
      (c) => `<option value="${escape(c.id)}"
              ${state.categoryId === c.id ? 'selected' : ''}>${escape(c.name)}</option>`,
    ),
  ].join('');
  const stakePills = STAKE_FILTERS.map(
    (f) => `<span class="pill ${state.stakeFilter === f.key ? 'on' : ''}"
              data-stake-key="${escape(f.key)}">${escape(f.label)}</span>`,
  ).join('');
  return `
    <div style="margin-top:14px">
      <select id="topic-filter" class="field-input"
              style="width:100%;font-size:13px;padding:10px 12px">
        ${topicOptions}
      </select>
    </div>
    <div class="row" style="gap:7px;margin-top:8px">${stakePills}</div>
  `;
}

function wireFilterHandlers(page, state) {
  const topicSelect = page.querySelector('#topic-filter');
  if (topicSelect) {
    topicSelect.addEventListener('change', async () => {
      state.categoryId = topicSelect.value || null;
      await refresh(page, state);
    });
  }
  page.querySelectorAll('[data-stake-key]').forEach((el) => {
    el.addEventListener('click', async () => {
      state.stakeFilter = el.dataset.stakeKey;
      await refresh(page, state);
    });
  });
}

function wireRowHandlers(page, state) {
  page.querySelectorAll('[data-join-id]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const duelId = btn.dataset.joinId;
      btn.disabled = true;
      btn.textContent = 'Joining…';
      try {
        await api(`/duels/${duelId}/join`, { method: 'POST' });
        navigate(`/duel/${duelId}/play`);
      } catch (err) {
        if (err instanceof ApiError && err.status === 409 && err.code === 'already_taken') {
          // Wireframe intent: swap the row in place, not a toast.
          const card = btn.closest('.card');
          if (card) {
            card.innerHTML = `<div class="row"><div class="grow"><b style="color:#fff;font-size:13px">Already taken</b>
              <div style="font-size:11px;color:var(--mute)">Someone else joined first — try another.</div></div></div>`;
          }
        } else if (err instanceof ApiError && err.code === 'insufficient_balance') {
          toast.error('Not enough balance to join that stake.');
          btn.disabled = false;
          btn.textContent = 'Join';
        } else {
          toast.error(err.message || 'Could not join.');
          btn.disabled = false;
          btn.textContent = 'Join';
        }
      }
    });
  });
  page.querySelectorAll('[data-play-id]').forEach((btn) => {
    btn.addEventListener('click', () => {
      navigate(`/duel/${btn.dataset.playId}/play`);
    });
  });
  page.querySelectorAll('[data-view-id]').forEach((btn) => {
    btn.addEventListener('click', () => {
      navigate(`/duel/${btn.dataset.viewId}`);
    });
  });
}

export function duelRowHtml(duel, kind /* 'your-turn' | 'open' | 'waiting' */) {
  const isYourTurn = kind === 'your-turn';
  const isWaiting = kind === 'waiting';
  const isOpen = kind === 'open';
  // For your-turn: show the opponent (creator, since I'm the joiner).
  // For open: show the creator.
  // For waiting: show who I'm waiting on (opponent if there is one,
  // else "anyone" — public duel not claimed yet).
  const otherPlayer = isWaiting ? (duel.opponent || null) : duel.creator;
  const rankLine = otherPlayer ? rankLabel(otherPlayer) : 'Open · public';
  const stakeLabel = duel.stake_kobo > 0 ? formatKobo(duel.stake_kobo) : 'Free';
  const deadline = isYourTurn || (isWaiting && duel.status === 'awaiting_opponent')
    ? duel.turn_deadline_at
    : duel.publish_expires_at;
  const countdown = fmtCountdown(deadline);
  const topicName = escape(duel.category_name || 'Trivia');
  let status;
  if (isYourTurn) status = `${topicName} · they've played`;
  else if (isWaiting) {
    status = duel.status === 'open'
      ? `${topicName} · waiting for a joiner`
      : `${topicName} · waiting for them to play`;
  } else status = `${topicName} · ${duel.question_count} questions`;
  let cardColor = '';
  let rankColor = '#5FE0BE';
  let name;
  if (isYourTurn) {
    cardColor = 'border-color:#5A431A;background:#241B0E';
    rankColor = '#F7C667';
    name = `vs ${escape(otherPlayer.username || 'Player')}`;
  } else if (isWaiting) {
    if (otherPlayer) {
      cardColor = 'border-color:#1E2A44;background:#141B2E';
      rankColor = '#7FB4FF';
      name = `You vs ${escape(otherPlayer.username || 'Player')}`;
    } else {
      // Seeking-joiner branch: violet family, and we lean on the
      // .seeking-duel class for the wrapper (no inline cardColor).
      rankColor = '#B8A6FF';
      name = 'Your public duel';
    }
  } else {
    name = escape(otherPlayer.username || 'Player');
  }
  let btn;
  if (isYourTurn) {
    btn = `<button class="btn gold sm" data-play-id="${escape(duel.id)}"
        style="margin:0;width:auto;padding:8px 20px">Play now</button>`;
  } else if (isWaiting) {
    btn = `<button class="btn ghost sm" data-view-id="${escape(duel.id)}"
        style="margin:0;width:auto;padding:8px 20px">View</button>`;
  } else {
    btn = `<button class="btn teal sm" data-join-id="${escape(duel.id)}"
        style="margin:0;width:auto;padding:8px 20px">Join</button>`;
  }

  const isSeeking = isWaiting && !otherPlayer;
  const countdownColor = isYourTurn
    ? '#F7C667'
    : isSeeking
      ? '#B8A6FF'
      : isWaiting
        ? '#7FB4FF'
        : 'var(--mute)';
  // Sub-30-min countdowns get a soft pulse in gold — nudges "act soon"
  // without shouting. Only applied to the open-to-join case; your-turn
  // rows already own the gold treatment for the whole row.
  const urgentClass = isOpen && _isSoonMs(deadline) ? 'pulse-urgent' : '';
  const stateChip = isOpen
    ? '<span class="chip-open">OPEN</span>'
    : isSeeking
      ? '<span class="chip-seeking">SEEKING JOINER</span>'
      : '';
  const cardClass = isOpen
    ? 'card open-duel'
    : isSeeking
      ? 'card seeking-duel'
      : 'card';
  return `
    <div class="${cardClass}" style="padding:12px;${cardColor}">
      <div class="row">
        <div class="av" style="width:40px;height:40px"></div>
        <div class="grow" style="min-width:0">
          <b style="font-size:14px;color:#fff;font-weight:600">${name}</b>
          <div style="font-size:11px;color:${rankColor};font-weight:600;margin-top:1px">${rankLine}</div>
        </div>
        <div style="text-align:right;flex:none">
          <div class="${urgentClass}" style="font-size:10px;color:${countdownColor};font-weight:600">${countdown}</div>
          <b style="font-size:16px;color:#fff;font-weight:700">${escape(stakeLabel)}</b>
        </div>
      </div>
      <div class="row" style="margin-top:10px">
        <div style="font-size:11.5px;color:var(--mute);font-weight:500">${stateChip}${status}</div>
        <div class="sp"></div>
        ${btn}
      </div>
    </div>
  `;
}

function _isSoonMs(iso) {
  if (!iso) return false;
  const ms = new Date(iso).getTime() - Date.now();
  return ms > 0 && ms <= 30 * 60_000;
}

export function rankLabel(player) {
  if (!player) return '';
  const tier = player.tier_key ? capitalize(player.tier_key) : '';
  const rank = player.rank ? `· #${player.rank.toLocaleString()}` : '';
  const text = [tier, rank].filter(Boolean).join(' ');
  return text || 'Unranked';
}

function capitalize(s) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function fmtCountdown(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const ms = then - now;
  if (ms <= 0) return 'ending…';
  const mins = Math.floor(ms / 60_000);
  if (mins < 60) return `${mins}m left`;
  const hrs = Math.floor(mins / 60);
  const rest = mins - hrs * 60;
  if (hrs < 24) return rest ? `${hrs}h ${rest}m left` : `${hrs}h left`;
  const days = Math.floor(hrs / 24);
  return `${days}d left`;
}
